/**
 * AuditReadinessDashboard - ISO 22301 Compliance Dashboard
 * Integrates BCI GPG with ISO 22301 audit requirements
 */
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Shield,
  FileSearch,
  AlertTriangle,
  FileText,
  ClipboardCheck,
  Target,
  CheckCircle2,
  XCircle,
  Info,
  ChevronRight,
  RefreshCw,
  Upload,
} from "lucide-react";
import { ISO_22301_MAPPING, PDCA_MAPPING, PP_EVIDENCE_CHECKLISTS } from "../data/isoMapping";

// PDCA Visual Diagram Component
function PDCADiagram({ complianceData }) {
  const phases = [
    { key: "plan", label: "PLAN", angle: -45, practices: ["PP1", "PP3"] },
    { key: "do", label: "DO", angle: 45, practices: ["PP2", "PP4", "PP5"] },
    { key: "check", label: "CHECK", angle: 135, practices: ["PP6"] },
    { key: "act", label: "ACT", angle: 225, practices: ["PP6"] },
  ];

  return (
    <div className="relative w-full max-w-md mx-auto aspect-square">
      {/* Center circle */}
      <div className="absolute inset-[30%] bg-gradient-to-br from-sky-700 to-teal-600 rounded-full flex items-center justify-center shadow-xl z-10">
        <div className="text-center text-white">
          <p className="text-xs font-medium opacity-80">ISO 22301</p>
          <p className="text-2xl font-bold">{complianceData.overall}%</p>
          <p className="text-xs opacity-80">Compliant</p>
        </div>
      </div>

      {/* PDCA Quadrants */}
      {phases.map((phase, index) => {
        const pdca = PDCA_MAPPING[phase.key];
        const phaseScore = complianceData.byPhase?.[phase.key] || 0;
        const isComplete = phaseScore >= 80;
        
        return (
          <div
            key={phase.key}
            className={`absolute w-[45%] h-[45%] rounded-2xl flex flex-col items-center justify-center p-3 transition-all hover:scale-105 cursor-pointer ${
              index === 0 ? "top-0 left-0" :
              index === 1 ? "top-0 right-0" :
              index === 2 ? "bottom-0 right-0" :
              "bottom-0 left-0"
            }`}
            style={{
              background: `linear-gradient(135deg, ${
                phase.key === "plan" ? "#3b82f6, #1d4ed8" :
                phase.key === "do" ? "#10b981, #059669" :
                phase.key === "check" ? "#f59e0b, #d97706" :
                "#8b5cf6, #7c3aed"
              })`
            }}
          >
            <p className="text-white font-bold text-lg">{phase.label}</p>
            <p className="text-white/80 text-xs text-center">{phase.practices.join(", ")}</p>
            <div className="mt-2 flex items-center gap-1">
              {isComplete ? (
                <CheckCircle2 className="w-4 h-4 text-white" />
              ) : (
                <span className="text-white font-semibold text-sm">{phaseScore}%</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Clause Lookup Component
function ClauseLookup({ onClauseClick }) {
  const [searchTerm, setSearchTerm] = useState("");
  
  const filteredClauses = ISO_22301_MAPPING.filter(item =>
    item.clause.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.mappedPP.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <input
        type="text"
        placeholder="Search by clause (e.g., 8.2.2) or PP..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-700"
      />
      <div className="max-h-[400px] overflow-y-auto space-y-2">
        {filteredClauses.map((item) => (
          <TooltipProvider key={item.clause}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="p-3 bg-white rounded-lg border border-slate-200 hover:border-sky-300 hover:bg-sky-50 cursor-pointer transition-all"
                  onClick={() => onClauseClick(item)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className="bg-sky-100 text-sky-700">{item.clause}</Badge>
                      <span className="font-medium text-slate-900">{item.title}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{item.mappedPP}</Badge>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-sm p-4">
                <p className="font-semibold text-sky-700 mb-2">Auditor&apos;s Perspective</p>
                <p className="text-sm text-slate-600">{item.auditFocus}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </div>
  );
}

// Evidence Checklist Component
function EvidenceChecklist({ pp, complianceStatus, onToggle, evidenceFiles, onFileUpload }) {
  const checklist = PP_EVIDENCE_CHECKLISTS[pp];
  const fileInputRefs = {};
  
  if (!checklist) return null;

  const handleFileSelect = (itemId, event) => {
    const file = event.target.files[0];
    if (file && onFileUpload) {
      onFileUpload(itemId, file);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-900">{checklist.name}</h4>
        <div className="flex gap-1">
          {checklist.isoClauses.map(clause => (
            <Badge key={clause} variant="outline" className="text-xs">{clause}</Badge>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {checklist.checklist.map((item) => {
          const isChecked = complianceStatus?.[item.id] || false;
          const hasEvidence = evidenceFiles?.[item.id];
          
          return (
            <TooltipProvider key={item.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      isChecked 
                        ? "bg-emerald-50 border-emerald-200" 
                        : "bg-white border-slate-200 hover:border-sky-200"
                    }`}
                  >
                    <div 
                      className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 cursor-pointer ${
                        isChecked ? "bg-emerald-500" : "border-2 border-slate-300"
                      }`}
                      onClick={() => onToggle(item.id)}
                    >
                      {isChecked && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>
                    <span className={`text-sm flex-1 ${isChecked ? "text-emerald-700" : "text-slate-700"}`}>
                      {item.text}
                    </span>
                    <div className="flex items-center gap-2">
                      {hasEvidence ? (
                        <Badge className="text-xs bg-blue-100 text-blue-700 gap-1">
                          <FileText className="w-3 h-3" />
                          Evidence
                        </Badge>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                            onChange={(e) => handleFileSelect(item.id, e)}
                          />
                          <Badge variant="outline" className="text-xs cursor-pointer hover:bg-slate-100 gap-1">
                            <Upload className="w-3 h-3" />
                            Attach
                          </Badge>
                        </label>
                      )}
                      <Badge className="text-xs bg-slate-100 text-slate-600">{item.clause}</Badge>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">Maps to ISO 22301 Clause {item.clause}</p>
                  {hasEvidence && <p className="text-xs text-emerald-600 mt-1">Evidence attached: {hasEvidence.name}</p>}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
}

// Audit Readiness Status Component  
function AuditReadinessStatus({ clause, status }) {
  const statusConfig = {
    ready: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50", label: "Ready for Audit" },
    partial: { icon: RefreshCw, color: "text-amber-500", bg: "bg-amber-50", label: "In Progress" },
    pending: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-50", label: "Not Started" }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg}`}>
      <Icon className={`w-4 h-4 ${config.color}`} />
      <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
    </div>
  );
}

// Main Component
export default function AuditReadinessDashboard({ embedded = false }) {
  const { api, user } = useAuth();
  const [complianceData, setComplianceData] = useState({
    overall: 0,
    byPP: {},
    byPhase: {},
    byClause: {},
    evidenceStatus: {}
  });
  const [evidenceFiles, setEvidenceFiles] = useState({});
  const [selectedClause, setSelectedClause] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    // Check if user has BCM Manager or Admin role
    if (user && user.role !== "bcm_manager" && user.role !== "admin") {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    fetchComplianceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleEvidenceUpload = (itemId, file) => {
    setEvidenceFiles(prev => ({
      ...prev,
      [itemId]: { name: file.name, file }
    }));
    // Also mark the item as checked when evidence is uploaded
    setComplianceData(prev => ({
      ...prev,
      evidenceStatus: { ...prev.evidenceStatus, [itemId]: true }
    }));
  };


  const fetchComplianceData = async () => {
    try {
      // Fetch compliance status from backend
      const response = await api.get("/compliance/status");
      const status = response.data;

      // Calculate compliance percentages based on actual data
      const pp1Score = status.pp1_policy?.score || 0;
      const pp2Score = status.pp2_training?.score || 0;
      const pp3BiaScore = status.pp3_bia?.score || 0;
      const pp3RiskScore = status.pp3_risk?.score || 0;
      const pp4Score = status.pp4_strategy?.score || 0;
      const pp5Score = status.pp5_plans?.score || 0;
      const pp6Score = status.pp6_exercises?.score || 0;

      // Map to PDCA phases
      const planScore = (pp1Score + pp3BiaScore + pp3RiskScore) / 3;
      const doScore = (pp2Score + pp4Score + pp5Score) / 3;
      const checkScore = pp6Score;
      const actScore = pp6Score; // Act phase is part of PP6

      // Map to ISO clauses
      const clauseStatus = {};
      ISO_22301_MAPPING.forEach(item => {
        let score = 0;
        if (item.mappedPP.includes("PP1")) score = pp1Score;
        else if (item.mappedPP.includes("PP2")) score = pp2Score;
        else if (item.mappedPP.includes("PP3")) score = (pp3BiaScore + pp3RiskScore) / 2;
        else if (item.mappedPP.includes("PP4")) score = pp4Score;
        else if (item.mappedPP.includes("PP5")) score = pp5Score;
        else if (item.mappedPP.includes("PP6")) score = pp6Score;
        
        clauseStatus[item.clause] = {
          score,
          status: score >= 80 ? "ready" : score >= 40 ? "partial" : "pending"
        };
      });

      setComplianceData({
        overall: Math.round(status.overall_score || 0),
        byPP: {
          PP1: pp1Score,
          PP2: pp2Score,
          PP3: (pp3BiaScore + pp3RiskScore) / 2,
          PP4: pp4Score,
          PP5: pp5Score,
          PP6: pp6Score
        },
        byPhase: {
          plan: Math.round(planScore),
          do: Math.round(doScore),
          check: Math.round(checkScore),
          act: Math.round(actScore)
        },
        byClause: clauseStatus,
        evidenceStatus: {}
      });
    } catch (error) {
      console.error("Failed to fetch compliance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEvidenceToggle = (itemId) => {
    setComplianceData(prev => ({
      ...prev,
      evidenceStatus: {
        ...prev.evidenceStatus,
        [itemId]: !prev.evidenceStatus[itemId]
      }
    }));
    // TODO: Save to backend
  };

  const handleClauseClick = (clause) => {
    setSelectedClause(clause);
    setActiveTab("details");
  };

  const ppIcons = {
    PP1: Shield,
    PP2: Target,
    PP3: FileSearch,
    PP4: AlertTriangle,
    PP5: FileText,
    PP6: ClipboardCheck
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Access denied for non-BCM Manager users
  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center h-96" data-testid="access-denied">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-600 text-center max-w-md mb-6">
          The ISO 22301 Audit Readiness Dashboard is only accessible to BCM Manageristrators.
          Please contact your organization&apos;s BCM Manager for access.
        </p>
        <Button
          onClick={() => window.location.href = "/dashboard"}
          className="bg-sky-700 hover:bg-sky-800"
        >
          Return to Dashboard
        </Button>
      </div>
    );
  }

  // Export Evidence Package function
  const handleExportEvidencePackage = () => {
    // Generate evidence package summary
    const evidencePackage = {
      generated_date: new Date().toISOString(),
      organization: "ResiliencyHUB Platform",
      compliance_score: complianceData.overall,
      by_professional_practice: complianceData.byPP,
      checklist_status: complianceData.evidenceStatus,
      attached_evidence: Object.keys(evidenceFiles).map(key => ({
        item_id: key,
        file_name: evidenceFiles[key]?.name,
      })),
      summary: {
        total_items: Object.keys(PP_EVIDENCE_CHECKLISTS).reduce((sum, pp) => 
          sum + PP_EVIDENCE_CHECKLISTS[pp].checklist.length, 0),
        completed_items: Object.values(complianceData.evidenceStatus).filter(v => v).length,
        evidence_attached: Object.keys(evidenceFiles).length,
      }
    };
    
    // Create downloadable JSON
    const blob = new Blob([JSON.stringify(evidencePackage, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ResiliencyHUB_Audit_Evidence_Package_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="audit-readiness-dashboard">
      {/* Header */}
      {!embedded && (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 9.2</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">ISO 22301 Audit Readiness</h1>
          <p className="text-slate-600 mt-1">Track compliance across BCI GPG and ISO 22301 requirements</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-sky-100 text-sky-700 px-4 py-2 text-sm">
            Overall Compliance: {complianceData.overall}%
          </Badge>
          <Button variant="outline" onClick={handleExportEvidencePackage}>
            <FileText className="w-4 h-4 mr-2" />
            Export Evidence Package
          </Button>
        </div>
      </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: "overview", label: "Overview" },
          { id: "clauses", label: "ISO Clause Lookup" },
          { id: "evidence", label: "Evidence Checklists" },
          { id: "details", label: "Clause Details" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === tab.id
                ? "text-sky-700 border-b-2 border-sky-700"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* PDCA Diagram */}
          <Card>
            <CardHeader>
              <CardTitle>BCM Lifecycle & PDCA Mapping</CardTitle>
              <CardDescription>
                Visual mapping of BCI GPG Professional Practices to ISO 22301 PDCA cycle
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PDCADiagram complianceData={complianceData} />
            </CardContent>
          </Card>

          {/* PP Compliance Cards */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900">Professional Practice Compliance</h3>
            {Object.entries(PP_EVIDENCE_CHECKLISTS).map(([pp, data]) => {
              const Icon = ppIcons[pp];
              const score = complianceData.byPP[pp] || 0;
              const status = score >= 80 ? "ready" : score >= 40 ? "partial" : "pending";
              
              return (
                <Card key={pp} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        status === "ready" ? "bg-emerald-100" :
                        status === "partial" ? "bg-amber-100" : "bg-slate-100"
                      }`}>
                        <Icon className={`w-6 h-6 ${
                          status === "ready" ? "text-emerald-600" :
                          status === "partial" ? "text-amber-600" : "text-slate-400"
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-slate-900">{pp}: {data.name}</span>
                          <span className="text-sm font-semibold text-slate-700">{Math.round(score)}%</span>
                        </div>
                        <Progress value={score} className="h-2" />
                        <div className="flex gap-1 mt-2">
                          {data.isoClauses.slice(0, 3).map(clause => (
                            <Badge key={clause} variant="outline" className="text-xs">{clause}</Badge>
                          ))}
                          {data.isoClauses.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{data.isoClauses.length - 3}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Clause Lookup Tab */}
      {activeTab === "clauses" && (
        <Card>
          <CardHeader>
            <CardTitle>ISO 22301 Clause Cross-Reference</CardTitle>
            <CardDescription>
              Click any clause to view its mapping to BCI GPG and audit evidence requirements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ClauseLookup onClauseClick={handleClauseClick} />
          </CardContent>
        </Card>
      )}

      {/* Evidence Checklists Tab */}
      {activeTab === "evidence" && (
        <div className="grid md:grid-cols-2 gap-6">
          {Object.entries(PP_EVIDENCE_CHECKLISTS).map(([pp, data]) => (
            <Card key={pp}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge className={`${
                    pp === "PP1" ? "bg-blue-100 text-blue-700" :
                    pp === "PP2" ? "bg-teal-100 text-teal-700" :
                    pp === "PP3" ? "bg-emerald-100 text-emerald-700" :
                    pp === "PP4" ? "bg-amber-100 text-amber-700" :
                    pp === "PP5" ? "bg-purple-100 text-purple-700" :
                    "bg-sky-100 text-sky-700"
                  }`}>{pp}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <EvidenceChecklist
                  pp={pp}
                  complianceStatus={complianceData.evidenceStatus}
                  onToggle={handleEvidenceToggle}
                  evidenceFiles={evidenceFiles}
                  onFileUpload={handleEvidenceUpload}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Clause Details Tab */}
      {activeTab === "details" && selectedClause && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Badge className="bg-sky-700 text-white px-4 py-2 text-lg">
                ISO 22301: {selectedClause.clause}
              </Badge>
              <AuditReadinessStatus
                clause={selectedClause.clause}
                status={complianceData.byClause[selectedClause.clause]?.status || "pending"}
              />
            </div>
            <CardTitle className="mt-4">{selectedClause.title}</CardTitle>
            <CardDescription>Mapped to: {selectedClause.ppName} ({selectedClause.mappedPP})</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Evidence Requirements */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileSearch className="w-5 h-5 text-sky-700" />
                Evidence Requirements
              </h4>
              <ul className="space-y-2">
                {selectedClause.evidence.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Auditor&apos;s Perspective */}
            <div className="bg-sky-50 rounded-xl p-6 border border-sky-100">
              <h4 className="font-semibold text-sky-900 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                Auditor&apos;s Perspective
              </h4>
              <p className="text-indigo-800">{selectedClause.auditFocus}</p>
            </div>

            {/* Full ISO 22301 Mapping Reference */}
            <div className="border rounded-xl overflow-hidden">
              <div className="bg-slate-800 text-white px-4 py-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  ISO 22301 to BCI GPG Mapping Reference
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b">ISO 22301 Clause</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b">ISO Requirement Summary</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b">Mapped BCI GPG Section</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b">Auditor&apos;s Focus (Evidence)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {ISO_22301_MAPPING.map((item, index) => (
                      <tr 
                        key={item.clause} 
                        className={`hover:bg-sky-50 cursor-pointer transition-colors ${
                          selectedClause?.clause === item.clause ? "bg-sky-100" : index % 2 === 0 ? "bg-white" : "bg-slate-50"
                        }`}
                        onClick={() => setSelectedClause(item)}
                      >
                        <td className="px-4 py-3">
                          <Badge className="bg-sky-100 text-sky-700">{item.clause}</Badge>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{item.title}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="font-semibold">{item.mappedPP}: {item.ppName}</Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs max-w-xs">
                          {item.evidence[0]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Navigation to PP */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                // Navigate to the relevant PP section
                const pp = selectedClause.mappedPP.split(" ")[0];
                if (pp === "PP1") window.location.href = "/policies";
                else if (pp === "PP2") window.location.href = "/training";
                else if (pp === "PP3") window.location.href = "/bia";
                else if (pp === "PP5") window.location.href = "/plans";
                else if (pp === "PP6") window.location.href = "/exercises";
              }}
            >
              Go to {selectedClause.ppName}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {activeTab === "details" && !selectedClause && (
        <div className="space-y-6">
          <Card>
            <CardContent className="py-8 text-center">
              <Info className="w-10 h-10 text-sky-500 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Click on any row below to view detailed clause information</p>
            </CardContent>
          </Card>

          {/* Full ISO 22301 Mapping Reference Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-sky-700" />
                ISO 22301 to BCI GPG Mapping Reference
              </CardTitle>
              <CardDescription>
                Complete mapping of ISO 22301 clauses to BCI Good Practice Guidelines Professional Practices
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">ISO 22301 Clause</th>
                      <th className="px-4 py-3 text-left font-semibold">ISO Requirement Summary</th>
                      <th className="px-4 py-3 text-left font-semibold">Mapped BCI GPG Section</th>
                      <th className="px-4 py-3 text-left font-semibold">Auditor&apos;s Professional Practice Details (Evidence &amp; Focus)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {ISO_22301_MAPPING.map((item, index) => (
                      <tr 
                        key={item.clause} 
                        className={`hover:bg-sky-50 cursor-pointer transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-slate-50"
                        }`}
                        onClick={() => setSelectedClause(item)}
                      >
                        <td className="px-4 py-3">
                          <Badge className="bg-sky-100 text-sky-700 font-mono">{item.clause}</Badge>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{item.title}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="font-semibold text-sky-700 border-sky-300">
                            {item.mappedPP}: {item.ppName}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">
                          {item.auditFocus.substring(0, 120)}...
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
